import ApplicationController from './application_controller';

/**
 * Availability Controller
 *
 * Checks if a value (username, email, phone) is available via server request.
 * Shows loading, available, or unavailable states.
 *
 * Usage:
 *   <div data-controller="availability" data-availability-url-value="/api/check/username">
 *     <input
 *       data-availability-target="input"
 *       data-action="blur->availability#check input->availability#debouncedCheck"
 *     />
 *     <span data-availability-target="loading" class="hidden">Checking...</span>
 *     <span data-availability-target="available" class="hidden text-green-700">Available</span>
 *     <span data-availability-target="unavailable" class="hidden text-red-600">Already taken</span>
 *   </div>
 *
 * Values:
 *   url: Server endpoint to check availability (required)
 *   param: Query parameter name (default: "value")
 *   debounce: Debounce delay in ms (default: 400)
 *   minLength: Minimum length before checking (default: 1)
 *
 * Targets:
 *   input: The input field to check (required)
 *   loading: Element shown while checking
 *   available: Element shown when value is available
 *   unavailable: Element shown when value is taken
 *
 * Server Response:
 *   JSON: { "available": true/false }
 *   Or HTML: Returns content to swap into unavailable target (for custom messages)
 */
export default class extends ApplicationController {
  static targets = ['input', 'loading', 'available', 'unavailable'];

  static values = {
    url: String,
    param: { type: String, default: 'value' },
    debounce: { type: Number, default: 400 },
    minLength: { type: Number, default: 1 },
  };

  connect() {
    this.debounceTimer = null;
    this.lastCheckedValue = null;
    this.isAvailable = null;
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  /**
   * Check availability immediately (use on blur)
   */
  check() {
    this.clearDebounce();
    this.performCheck();
  }

  /**
   * Check availability with debounce (use on input)
   */
  debouncedCheck() {
    this.clearDebounce();
    this.debounceTimer = setTimeout(() => {
      this.performCheck();
    }, this.debounceValue);
  }

  /**
   * Perform the availability check
   */
  async performCheck() {
    const value = this.inputTarget.value.trim();

    // Skip if empty or too short
    if (value.length < this.minLengthValue) {
      this.hideAll();
      this.isAvailable = null;
      return;
    }

    // Skip if same as last check
    if (value === this.lastCheckedValue) {
      return;
    }

    this.lastCheckedValue = value;
    this.showLoading();

    try {
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set(this.paramValue, value);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      this.isAvailable = data.available;

      if (data.available) {
        this.showAvailable();
      } else {
        this.showUnavailable(data.message);
      }
    } catch (error) {
      console.error('Availability check failed:', error);
      // On error, hide feedback and allow form submission
      this.hideAll();
      this.isAvailable = null;
    }
  }

  /**
   * Returns whether the current value is available
   * Can be called from other controllers or form validation
   */
  getAvailability() {
    return this.isAvailable;
  }

  /**
   * Reset the controller state
   */
  reset() {
    this.hideAll();
    this.lastCheckedValue = null;
    this.isAvailable = null;
  }

  // --- Private methods ---

  clearDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  hideAll() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add('hidden');
    if (this.hasAvailableTarget) this.availableTarget.classList.add('hidden');
    if (this.hasUnavailableTarget) this.unavailableTarget.classList.add('hidden');
  }

  showLoading() {
    this.hideAll();
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove('hidden');
  }

  showAvailable() {
    this.hideAll();
    if (this.hasAvailableTarget) this.availableTarget.classList.remove('hidden');
    this.inputTarget.classList.remove('border-red-500');
    this.inputTarget.classList.add('border-green-500');
  }

  showUnavailable(message) {
    this.hideAll();
    if (this.hasUnavailableTarget) {
      if (message) {
        this.unavailableTarget.textContent = message;
      }
      this.unavailableTarget.classList.remove('hidden');
    }
    this.inputTarget.classList.remove('border-green-500');
    this.inputTarget.classList.add('border-red-500');
  }
}
